import { MoveDown } from "lucide-react";
import { cn } from "web-design-system";
import {
  PortfolioReview,
  PortfolioReviewAction,
  PortfolioReviewLine,
} from "creso-core/types";
import {
  convertToCurrency,
  formatAsPercentage,
  formatShort,
} from "creso-core/utils";
import {
  FundCell,
  QuartileBadge,
  SourceBadge,
} from "@/modules/portfolio-review/components/workbench/fund-badges";
import {
  ActionPill,
  ReportPageTitle,
  ReportStats,
  TotalPill,
} from "./report-blocks";
import { lineReason, linesIn } from "./report-data";

const COLUMNS = ["Source", "3Y Quartile", "XIRR", "Invested", "Current"];

const sumOf = (lines: PortfolioReviewLine[]) =>
  lines.reduce(
    (total, line) => total + Number(line.amount ?? line.current_value),
    0
  );

export function RecommendationsSummary({
  review,
}: {
  review: PortfolioReview;
}) {
  const { tax, amount_moved } = review.insights;
  const moving = review.lines.filter(
    (line) => line.bucket === "SWITCH" || line.bucket === "EXIT"
  ).length;

  return (
    <div className="grid grid-cols-[2fr_3fr] gap-6">
      <ReportStats
        stats={[
          { label: "Switch and Exit", value: `${moving} funds` },
          { label: "Amount moved", value: formatShort(amount_moved) },
        ]}
      />
      <ReportStats
        stats={[
          { label: "STCG Tax", value: convertToCurrency(tax.stcg_tax) },
          { label: "LTCG Tax", value: convertToCurrency(tax.ltcg_tax) },
          {
            label: "Total tax liability",
            value: convertToCurrency(tax.total_tax),
          },
        ]}
      />
    </div>
  );
}

export function FundsToHoldPage({ review }: { review: PortfolioReview }) {
  return (
    <div className="flex flex-col gap-6">
      <ReportPageTitle title="Funds to hold" subsection />
      <LinesTable review={review} lines={linesIn(review, "HOLD")} />
    </div>
  );
}

export function FundsToSwitchPage({ review }: { review: PortfolioReview }) {
  const lines = linesIn(review, "SWITCH");
  return (
    <div className="flex flex-col break-inside-avoid gap-6">
      <ReportPageTitle
        title="Funds to switch"
        subsection
        aside={
          <TotalPill
            label="Total switched"
            value={convertToCurrency(sumOf(lines))}
          />
        }
      />
      <LinesTable review={review} lines={lines} />
    </div>
  );
}

export function FundsToExitPage({ review }: { review: PortfolioReview }) {
  const additions = linesIn(review, "NEW_ADDITION");
  return (
    <div className="flex flex-col break-inside-avoid gap-6">
      <ReportPageTitle
        title="Funds to exit and new additions"
        subsection
        aside={
          additions.length ? (
            <TotalPill
              label="Total re-invested"
              value={convertToCurrency(sumOf(additions))}
            />
          ) : null
        }
      />
      <LinesTable
        review={review}
        lines={[...linesIn(review, "EXIT"), ...additions]}
      />
    </div>
  );
}

function LinesTable({
  review,
  lines,
}: {
  review: PortfolioReview;
  lines: PortfolioReviewLine[];
}) {
  // A new addition the client already holds elsewhere reads as "Existing".
  const heldIsins = new Set(
    review.lines
      .filter((line) => line.bucket !== "NEW_ADDITION")
      .map((line) => line.source_isin)
  );

  return (
    <div className="overflow-hidden rounded-lg border border-border">
      <table className="w-full text-sm">
        <thead className="bg-card">
          <tr>
            <th className="p-4 text-left font-semibold">Fund Name</th>
            {COLUMNS.map((column, index) => (
              <th
                key={column}
                className={cn(
                  "whitespace-nowrap p-4 font-semibold",
                  index > 1 ? "text-right" : "text-left"
                )}
              >
                {column}
              </th>
            ))}
            <th className="p-4 text-center font-semibold">Action</th>
          </tr>
        </thead>
        {lines.map((line) => (
          <LineRows
            key={line.id}
            line={line}
            existing={heldIsins.has(line.target_isin)}
          />
        ))}
      </table>
    </div>
  );
}

function LineRows({
  line,
  existing,
}: {
  line: PortfolioReviewLine;
  existing: boolean;
}) {
  const isNew = line.bucket === "NEW_ADDITION";
  const isSwitch = line.bucket === "SWITCH";
  const muted = isSwitch && "opacity-50";
  const reason = lineReason(line);
  const xirr = line.xirr === null ? null : Number(line.xirr);

  return (
    <tbody className="border-t border-border">
      <tr>
        <td className={cn("px-4 py-3", muted)}>
          <FundCell
            name={isNew ? line.target_scheme_name : line.source_scheme_name}
            category={isNew ? line.target_category : line.source_category}
            amcCode={
              isNew
                ? line.target_amc_morningstar_code
                : line.amc_morningstar_code
            }
          />
        </td>
        <td className={cn("px-4 py-3", muted)}>
          <SourceBadge
            label={isNew ? (existing ? "Existing" : "New") : line.source_label}
          />
        </td>
        <td className={cn("px-4 py-3", muted)}>
          <QuartileBadge
            quartile={isNew ? line.target_quartile : line.quartile}
          />
        </td>
        <td className={cn("px-4 py-3 text-right", muted)}>
          {xirr === null || isNew ? (
            "-"
          ) : (
            <span
              className={xirr >= 0 ? "text-constructive" : "text-destructive"}
            >
              {formatAsPercentage(xirr)}
            </span>
          )}
        </td>
        <td className={cn("px-4 py-3 text-right", muted)}>
          {isNew ? "-" : convertToCurrency(line.invested_value)}
        </td>
        <td className={cn("px-4 py-3 text-right", muted)}>
          {convertToCurrency(isNew ? (line.amount ?? 0) : line.current_value)}
        </td>
        <td className="px-4 py-3 text-center">
          <ActionPill
            action={
              line.action ??
              (isNew ? "ADD" : (line.bucket as PortfolioReviewAction))
            }
          />
        </td>
      </tr>
      {reason || isSwitch ? (
        <tr>
          <td className="px-4 pb-3">
            {isSwitch ? (
              <span className="flex size-8 items-center justify-center rounded-md bg-card">
                <MoveDown className="size-4 text-muted-foreground" />
              </span>
            ) : null}
          </td>
          <td colSpan={COLUMNS.length + 1} className="pb-3 pr-4">
            {reason ? (
              <div className="rounded-lg bg-muted px-4 py-3 text-xs text-muted-foreground">
                {reason}
              </div>
            ) : null}
          </td>
        </tr>
      ) : null}
      {isSwitch && line.target_scheme_name ? (
        <tr>
          <td className="px-4 pb-3">
            <FundCell
              name={line.target_scheme_name}
              category={line.target_category}
              amcCode={line.target_amc_morningstar_code}
            />
          </td>
          <td className="px-4 pb-3">
            <SourceBadge label="New" />
          </td>
          <td className="px-4 pb-3">
            <QuartileBadge quartile={line.target_quartile} />
          </td>
          <td className="px-4 pb-3 text-right">-</td>
          <td className="px-4 pb-3 text-right">-</td>
          <td className="px-4 pb-3 text-right">
            {convertToCurrency(line.amount ?? line.current_value)}
          </td>
          <td />
        </tr>
      ) : null}
    </tbody>
  );
}
